import { v4 as uuidV4 } from 'uuid';
import type { MachineConfig } from 'xstate';
import { Machine } from 'xstate';

import { EmptyTrackingDesignConfig } from 'src/modules/designStudio/constants';
import { createEmptyTemplateDesignConfig } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { options } from 'src/modules/designStudio/machines/history/options';
import { Mode } from 'src/modules/designStudio/providers/ModeProvider';
import type { SliceOfToast } from 'src/shared/components/Toast/types';
import type { WrappedSplitClient } from 'src/shared/hooks/useSplit/types';
import type { Bread } from 'src/shared/providers/Toaster';

import type { Context, Event, InitialContext, Schema } from './types';

export const initialContext = {
  email: null,
  userId: null,
  buildingModel: null,
  jobId: null,
  history: null,
  currentVersion: null,
  designConfig: createEmptyTemplateDesignConfig(),
  trackingDesignConfig: { ...EmptyTrackingDesignConfig },
  currentIndexInHistory: undefined,
  canUndo: false,
  canRedo: false,
  changeSinceLastNotification: false,
  translator: () => '',
  mode: Mode.DEFAULT,
  designUpdates: 0,
  splitClient: Promise.resolve({
    getTreatment: () => undefined,
    getTreatmentWithConfig: () => undefined,
  } as WrappedSplitClient),
  inspireMeImageURL: '',
  inspireMeDesignNameToRestore: '',
  inspireMeFrozenRenderURL: '',
  inspireMePastImageURLs: [],
};

const createMachineConfig = (
  jobId: number | null,
  translator: (text: string) => string,
  splitClient: Promise<WrappedSplitClient>,
  authToken?: string | null,
  shareToken?: string,
  addToast?: (bread: Bread) => void,
  removeToast?: (toast: SliceOfToast, loaf: SliceOfToast[]) => void,
  viewUserId?: number,
  mode: Mode = Mode.DEFAULT,
): MachineConfig<Context, Schema, Event> => ({
  id: 'history',
  context: {
    ...initialContext,
    sessionId: uuidV4(),
    jobId,
    authToken,
    shareToken,
    addToast,
    removeToast,
    canRedo: false,
    canUndo: false,
    translator,
    changeSinceLastNotification: false,
    viewUserId,
    mode,
    splitClient,
  },
  initial: 'setup',
  states: {
    setup: {
      invoke: {
        src: 'setupAndFetchModelReference',
        onDone: [
          {
            target: 'afterSetup',
            actions: ['setupCompletion'],
            cond: 'isShareView',
          },
          {
            target: 'fetchCurrentVersion',
            actions: ['setupCompletion'],
          },
        ],
        onError: {
          target: 'notConnectedToFirestore',
          actions: 'logError',
        },
      },
    },
    fetchCurrentVersion: {
      invoke: {
        src: 'fetchCurrentVersion',
        onDone: {
          target: 'afterSetup',
          actions: ['sendDoneFetchingLatestVersionEvent'],
        },
        onError: {
          target: 'afterSetup',
          actions: 'logError',
        },
      },
    },
    afterSetup: {
      type: 'parallel',
      on: {
        UPDATE_UNDO_REDO_STATE: {
          actions: 'updateCanUndoAndRedo',
        },
      },
      states: {
        listeningForNewVersions: {
          invoke: {
            src: 'fetchVersions',
            onError: {
              actions: 'logError',
            },
          },
          on: {
            SET_HISTORY: {
              actions: ['setHistory', 'updateUndoRedoState'],
            },
          },
        },
        notifySavingChanges: {
          on: {
            NOTIFY_SAVE: {
              actions: ['sendSavingToast', 'resetChangeSinceNotification'],
              cond: 'shouldNotifySave',
            },
          },
          invoke: {
            src: 'setupNotifierService',
            onError: { actions: 'logError' },
          },
        },
        waitingForDesignChanges: {
          initial: 'idle',
          states: {
            updatingDesign: {
              invoke: {
                src: 'createVersion',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            undoDesign: {
              invoke: {
                src: 'markPreviousDesignAsCurrent',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            redoDesign: {
              invoke: {
                src: 'markNextDesignAsCurrent',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            createInspireMeImage: {
              invoke: {
                src: 'requestInspireMeImage',
                onDone: {
                  target: 'idle',
                  actions: 'assignInspireMeImage',
                },
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            waitForInspireMeImage: {
              invoke: {
                src: 'spinBeforeRequestInspireMeImage',
                onDone: {
                  target: 'createInspireMeImage',
                  actions: ['assignFrozenInspireMeImage'],
                },
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            idle: {
              on: {
                UNDO_DESIGN_CHANGE: {
                  target: 'undoDesign',
                  cond: 'canUndo',
                  // these are optimistic operations
                  actions: 'undoDesignAction',
                },
                REDO_DESIGN_CHANGE: {
                  target: 'redoDesign',
                  cond: 'canRedo',
                  // these are optimistic operations
                  actions: 'redoDesignAction',
                },
                UPDATE_DESIGN_FROM_BUILDING: {
                  target: 'updatingDesign',
                  actions: ['updateDesignConfigFromBuilding'],
                },
                UPDATE_DESIGN_LEGACY: {
                  target: 'updatingDesign',
                  actions: [
                    'updateDesignConfigOptimistically',
                    'markChangeSinceNotification',
                    'trackUpdate',
                  ],
                },
                RESET_DESIGN: {
                  target: 'updatingDesign',
                  actions: [
                    'updateDesignConfigOptimistically',
                    'markChangeSinceNotification',
                  ],
                },
                CREATE_INSPIRE_ME_IMAGE: {
                  target: 'createInspireMeImage',
                },
                CLEAR_INSPIRE_ME_IMAGE: {
                  target: 'idle',
                  actions: 'clearInspireMeImage',
                },
                WAIT_FOR_INSPIRE_ME_IMAGE: {
                  target: 'waitForInspireMeImage',
                },
                SET_INSPIRE_ME_IMAGE: {
                  target: 'idle',
                  actions: 'setInspireMeImage',
                },
              },
            },
          },
        },
      },
    },
    notConnectedToFirestore: {
      type: 'final',
    },
  },
  exit: 'teardownFirebaseApp',
});

const createMachine = ({
  jobId,
  authToken,
  shareToken,
  addToast,
  removeToast,
  translator,
  viewUserId,
  mode,
  splitClient,
}: InitialContext) =>
  Machine<Context, Schema, Event>(
    createMachineConfig(
      jobId,
      translator,
      splitClient,
      authToken,
      shareToken,
      addToast,
      removeToast,
      viewUserId,
      mode,
    ),
    options,
  );

export { createMachine, createMachineConfig };
